import {
  makeStyles,
  Theme,
  createStyles,
  Box,
  BoxProps,
  Typography,
  withStyles,
  SvgIcon,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { formatRelative } from "date-fns";
import { enUS } from "date-fns/esm/locale";
import clsx from "clsx";

import { Button } from "~components/Button";
import { Tick } from "~components/Icons";
import { EmptyState, EmptyStatePlaceholder } from "./emptyState";

type Step = {
  title: string;
  subtitle: string;
  completedAt?: Date;
  action?: () => void;
};

export type OnboardingProps = {
  steps: Step[];
  isLoading: boolean;
} & EmptyStatePlaceholder &
  BoxProps;

export const Onboarding = ({ steps, isLoading, emptyPlaceholder, ...props }: OnboardingProps) => {
  const classes = useStyles();

  if (isLoading) {
    return (
      <Box {...props} className={classes.root}>
        <Typography className={classes.title}>Boarding checklist</Typography>
        <Box className={classes.stepWrapper}>
          {Array.from(Array(steps.length ?? 3).keys()).map((idx) => (
            <Box display="flex" margin={2} style={{ backgroundColor: "white" }} key={idx}>
              <Skeleton
                variant="circle"
                width={36.6}
                height={33}
                style={{ margin: 16, marginLeft: 20 }}
              />
              <Box width="100%">
                <Box display="flex" justifyContent="space-between">
                  <Skeleton width={80} height={18} style={{ marginTop: 16, marginLeft: 0 }} />
                  <Skeleton width={80} height={18} style={{ marginTop: 16, marginRight: 16 }} />
                </Box>
                <Skeleton width={250} height={18} style={{ marginBottom: 20, marginLeft: 0 }} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box {...props} className={classes.root}>
      <Typography className={classes.title}>Boarding checklist</Typography>
      {!steps || steps?.length <= 0 ? (
        <EmptyState emptyPlaceholder={emptyPlaceholder} />
      ) : (
        <Box className={classes.stepWrapper}>
          {steps.map((step, index) => (
            <Box
              className={
                step.completedAt
                  ? classes.stepContainer
                  : clsx(classes.stepContainer, classes.stepContainerOpen)
              }
            >
              <Box>
                <Box className={classes.index}>
                  {step.completedAt ? (
                    <SvgIcon className={classes.tick} color="inherit">
                      <Tick />
                    </SvgIcon>
                  ) : (
                    <Box marginTop="2px">
                      <Typography className={classes.number}>{`0 ${index + 1}`}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box>
                <Typography className={classes.stepTitle}>{step.title}</Typography>
                <Typography className={classes.subtitle}>{step.subtitle}</Typography>
              </Box>
              <Box marginLeft="auto">
                {step.completedAt ? (
                  <Box className={classes.completedContainer}>
                    <Box className={classes.completedTick}>
                      <SvgIcon className={classes.tick}>
                        <Tick />
                      </SvgIcon>
                    </Box>
                    <Typography className={classes.completedAt}>
                      {formatRelative(new Date(), step.completedAt, {
                        locale: {
                          ...enUS,
                          formatRelative: (token: keyof typeof formatRelativeLocale) =>
                            formatRelativeLocale[token],
                        },
                      })}
                    </Typography>
                  </Box>
                ) : (
                  <SmallButton onClick={step.action}>Setup</SmallButton>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const SmallButton = withStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: `${theme.spacing(1.5)}px ${theme.spacing(2)}px`,
    },
  }),
)(Button);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
    },
    title: {
      fontSize: 22,
      fontWeight: 600,
      lineHeight: "29px",
      color: theme.palette.dark.main,
      paddingBottom: theme.spacing(3),
    },
    box: {
      width: "100%",
      borderRadius: 4,
      boxShadow: "0 1px 1px 0 rgba(32, 37, 43, 0.12), 0 0 1px 0 rgba(32, 37, 43, 0.1)",
      backgroundColor: "white",
    },
    image: {
      width: "100%",
      height: "100%",
    },
    stepWrapper: {
      "& > div": { margin: 0 },
      "& > div:not(:last-child):not(:first-child)": {
        margin: "16px 0",
      },
      "& > div:not(:last-child)": {
        position: "relative",
        "&::after": {
          display: "block",
          content: "''",
          position: "absolute",
          bottom: -16,
          left: 40,
          height: "16px",
          width: "1px",
          backgroundColor: theme.palette.blueGrey.main,
        },
      },
    },
    stepContainer: {
      minWidth: 612,
      padding: `${theme.spacing(2)}px ${theme.spacing(2.5)}px ${theme.spacing(1)}px`,
      borderRadius: 4,
      boxShadow: "0 6px 12px 0 rgba(0, 0, 0, 0.02)",
      backgroundColor: "#fdfdfe",
      display: "flex",
    },
    stepContainerOpen: {
      boxShadow: `0 10px 12px 0 rgba(32, 37, 43, 0.12), 0 0 1px 0 rgba(10, 31, 68, 0.1), inset 2px 0 0 0 ${theme.palette.primary.main}`,
      backgroundColor: "white",
    },
    index: {
      width: theme.spacing(5),
      height: theme.spacing(5),
      borderRadius: theme.spacing(5),
      marginRight: theme.spacing(2.5),
      padding: theme.spacing(1),
      boxShadow: "0 4px 8px 0 rgba(32, 37, 43, 0.12), 0 0 1px 0 rgba(32, 37, 43, 0.1)",
      backgroundColor: "white",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      color: theme.palette.success.main,
    },
    tick: {
      marginLeft: -2,
      marginTop: -2,
      width: "unset",
      height: "unset",
    },
    number: {
      fontSize: 16,
      fontWeight: 600,
      color: theme.palette.primary.main,
    },
    stepTitle: {
      fontSize: 16,
      lineHeight: "24px",
      color: theme.palette.dark.main,
    },
    subtitle: {
      fontSize: 14,
      lineHeight: "24px",
      color: theme.palette.battleshipGrey.main,
    },
    completedAt: {
      fontSize: 10,
      lineHeight: "25px",
      letterSpacing: 1,
      textAlign: "right",
      color: theme.palette.battleshipGrey.main,
      textTransform: "uppercase",
    },
    completedContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    completedTick: {
      width: 16,
      height: 16,
      borderRadius: 16,
      boxShadow: "0 4px 6px 0 rgba(3, 100, 255, 0.3)",
      backgroundColor: theme.palette.primary.main,
      color: "white",
      marginRight: theme.spacing(1.5),
      padding: 2,
      fontSize: 10,
    },
  }),
);

const formatRelativeLocale = {
  lastWeek: "'last' eeee HH:mm",
  yesterday: "'yesterday' HH:mm",
  today: "'today' HH:mm",
  tomorrow: "'tomorrow' HH:mm",
  nextWeek: "eeee HH:mm",
  other: "P",
};
